import { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, CheckCircle2, XCircle, Clock, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../../api/client';
import { useAuthStore } from '../../../store/authStore';
import { ROUTES } from '../../../api/routes';

interface Incidente {
  id: string;
  tipo: string;
  severidade: 'MEDIA' | 'ALTA' | 'CRITICA';
  acao: string;
  ip: string | null;
  local: string | null;
  userAgent: string | null;
  status: 'PENDENTE' | 'CONFIRMADO_USUARIO' | 'BLOQUEADO';
  resolvido: boolean;
  criadoEm: string;
  resolvidoEm: string | null;
  usuario?: { id: string; nome: string; email: string; cargo: string | null };
}

const TIPO_LABEL: Record<string, string> = {
  IP_NOVO_PAIS: 'Acesso de país diferente',
  IP_NOVO_ESTADO: 'Acesso de estado diferente',
  MULTIPLAS_FALHAS: 'Múltiplas tentativas falhas',
  DISPOSITIVO_DESCONHECIDO: 'Dispositivo desconhecido',
  HORARIO_ATIPICO: 'Horário atípico',
  SESSAO_SIMULTANEA: 'Sessão simultânea',
  TOKEN_INVALIDO_REPETIDO: 'Token inválido repetido',
};

const SEV_COLOR: Record<string, string> = {
  MEDIA: '#f59e0b',
  ALTA: '#ef4444',
  CRITICA: '#7c3aed',
};

const STATUS_CONFIG = {
  PENDENTE: { label: 'Pendente', color: '#f59e0b', icon: <Clock size={12} /> },
  CONFIRMADO_USUARIO: { label: 'Confirmado', color: '#10b981', icon: <CheckCircle2 size={12} /> },
  BLOQUEADO: { label: 'Bloqueado', color: '#ef4444', icon: <XCircle size={12} /> },
};

/**
 * IncidentesSegurancaPage — Dashboard de incidentes de segurança.
 * Usado pelo Diretor e pelo Admin Master.
 *
 * Doc: docs_autentica_seguranca_hard/09-anomalia-incidente.md
 */
export function IncidentesSegurancaPage() {
  const escolaId = useAuthStore(s => s.escolaId);
  const [incidentes, setIncidentes] = useState<Incidente[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [acao, setAcao] = useState<{ id: string; tipo: 'confirmar' | 'bloquear' } | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(ROUTES.security.incidentesEscola(escolaId!));
      setIncidentes(response.data ?? []);
    } catch {
      setIncidentes([]);
    } finally {
      setLoading(false);
    }
  }, [escolaId]);

  useEffect(() => { carregar(); }, [carregar]);

  const executarAcao = async (id: string, tipo: 'confirmar' | 'bloquear') => {
    setAcao({ id, tipo });
    try {
      await api.post(ROUTES.security.incidenteAcao(id, tipo), {});
      await carregar();
    } finally {
      setAcao(null);
    }
  };

  const pendentes = incidentes.filter(i => !i.resolvido).length;
  const criticos = incidentes.filter(i => i.severidade === 'CRITICA').length;

  const card: React.CSSProperties = {
    background: 'var(--bg-card)',
    borderRadius: 16,
    border: '1px solid var(--border-color)',
    padding: '1.5rem',
  };

  return (
    <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={28} style={{ color: '#6c5ce7' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)' }}>
              Incidentes de Segurança
            </h1>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Últimos 30 dias — {incidentes.length} registros
            </p>
          </div>
        </div>
        <button
          onClick={carregar}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.82rem' }}
        >
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16 }}>
        {[
          { label: 'Total', value: incidentes.length, color: '#6c5ce7' },
          { label: 'Pendentes', value: pendentes, color: '#f59e0b' },
          { label: 'Críticos', value: criticos, color: '#7c3aed' },
          { label: 'Resolvidos', value: incidentes.length - pendentes, color: '#10b981' },
        ].map(k => (
          <div key={k.label} style={{ ...card, padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Lista */}
      <div style={card}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
            <p>Carregando incidentes...</p>
          </div>
        ) : incidentes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <CheckCircle2 size={40} style={{ margin: '0 auto 1rem', color: '#10b981' }} />
            <p style={{ fontWeight: 700 }}>Nenhum incidente registrado nos últimos 30 dias.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {incidentes.map((inc, idx) => {
              const statusCfg = STATUS_CONFIG[inc.status];
              const isExp = expandido === inc.id;
              return (
                <div key={inc.id} style={{
                  borderBottom: idx < incidentes.length - 1 ? '1px solid var(--border-color)' : 'none',
                  padding: '14px 0',
                }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                    onClick={() => setExpandido(isExp ? null : inc.id)}
                  >
                    {/* Severidade */}
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: SEV_COLOR[inc.severidade],
                      boxShadow: `0 0 6px ${SEV_COLOR[inc.severidade]}88`,
                    }} />

                    {/* Tipo */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main)' }}>
                        {TIPO_LABEL[inc.tipo] ?? inc.tipo}
                      </div>
                      {inc.usuario && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {inc.usuario.nome} {inc.usuario.cargo ? `— ${inc.usuario.cargo}` : ''}
                        </div>
                      )}
                    </div>

                    {/* Data */}
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(inc.criadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>

                    {/* Status badge */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px',
                      borderRadius: 50, fontSize: '0.72rem', fontWeight: 700,
                      background: `${statusCfg.color}18`, color: statusCfg.color,
                      whiteSpace: 'nowrap',
                    }}>
                      {statusCfg.icon} {statusCfg.label}
                    </div>

                    {isExp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>

                  {/* Detalhes expandidos */}
                  {isExp && (
                    <div style={{
                      marginTop: 12, marginLeft: 20, padding: '14px 16px',
                      borderRadius: 12, background: 'var(--bg-muted)',
                      display: 'flex', flexDirection: 'column', gap: 10,
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.78rem' }}>
                        {inc.ip && <div><span style={{ color: 'var(--text-muted)' }}>IP: </span><strong>{inc.ip}</strong></div>}
                        {inc.local && <div><span style={{ color: 'var(--text-muted)' }}>Local: </span><strong>{inc.local}</strong></div>}
                        <div><span style={{ color: 'var(--text-muted)' }}>Severidade: </span>
                          <strong style={{ color: SEV_COLOR[inc.severidade] }}>{inc.severidade}</strong>
                        </div>
                        <div><span style={{ color: 'var(--text-muted)' }}>Ação: </span><strong>{inc.acao.replace(/_/g, ' ')}</strong></div>
                      </div>

                      {!inc.resolvido && (
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                          <button
                            onClick={() => executarAcao(inc.id, 'confirmar')}
                            disabled={acao?.id === inc.id}
                            style={{
                              flex: 1, padding: '9px', borderRadius: 10, border: '1px solid #10b98155',
                              background: '#10b98111', color: '#10b981', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem',
                            }}
                          >
                            ✅ Fui eu — Reconheço
                          </button>
                          <button
                            onClick={() => executarAcao(inc.id, 'bloquear')}
                            disabled={acao?.id === inc.id}
                            style={{
                              flex: 1, padding: '9px', borderRadius: 10, border: 'none',
                              background: '#ef4444', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem',
                            }}
                          >
                            ❌ Não fui eu — Bloquear
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
